/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { TOOLBAR_BUTTON_SIZE, ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { ACTION_BUTTON_STYLE } from "../../../../ui/components/ActionButton";
import { ReadmeModel } from "../../Common/ReadmeModel";
import { isSchemaConstObjectModel } from "@adapptio/lib-blueprint";
import { ReadmeEditSelection } from "../../Common/ReadmeEditSelection";

function createAddMenu(readmeModel: ReadmeModel, selection: ReadmeEditSelection): IDropdownMenuItem[] {
	const addPage = () => {
		const pagesModel = readmeModel.getModel().props.spec.props.pages;
		const page = pagesModel.schema.addElement(pagesModel, undefined, true);

		if (isSchemaConstObjectModel(page)) {
			selection.selectPage(pagesModel, page);
		}
	};

	return [
		{
			id: "page",
			label: "Add Page",
			icon: "mdi/file-document-outline",
			onClick: () => addPage()
		}
	];
}

export interface IReadmeEditorOutlineToolbarProps {
	className?: string;
	readmeModel: ReadmeModel;
}

export const ReadmeEditorOutlineToolbar: React.FC<IReadmeEditorOutlineToolbarProps> = ({
	className,
	readmeModel
}) => {
	const baseClass = BEM("edt-readme-editor-outline-toolbar");

	const addMenuItems = useMemo(() => createAddMenu(readmeModel, readmeModel.getSelection()), [ readmeModel ]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<>
			<ToolbarTitle>Outline</ToolbarTitle>
			<ToolbarSpacer grow={1} />
			<ToolbarButton
				icon="mdi/plus"
				size={TOOLBAR_BUTTON_SIZE.SMALL}
				menuItems={addMenuItems}
				menuStyle={DROPDOWN_STYLE.COMPACT}
				style={ACTION_BUTTON_STYLE.PRIMARY}
			/>
		</>
	);
};
